/**
 * Static choice lists and label formatting for the schedule pattern editor.
 *
 * The v2 choice controls (`wb-button-tab`, `wb-button-chip`, `wb-button-select`) all take a `Command[]`
 * whose `data` is the value they emit, so every option list in the editor is built the same way here
 * rather than being spelled out in the template. Labels are trade language, never RRULE tokens.
 */

import { Command } from '@workbuddy/ui/model';
import { ScheduleAfterServiceAnchor, ScheduleKind, ScheduleNonWorkingDayPolicy, ScheduleNth, ScheduleRepeat, ScheduleWeekday } from '@jms/model/scheduling';

/** Builds one option. `data` carries the value so the control emits the value, not an id string. */
function option<T>(id: string, label: string, data: T): Command {

  return new Command({ _id: id, label, data });
}

/**
 * The three kinds, Calendar first.
 *
 * Calendar leads because it is what a scheduler reaches for: a task with no schedule of its own is
 * already due every working day, so opening the editor is almost always an intent to name dates. Per
 * visit is last for the same reason — it is the tack-on exception, not the starting point.
 */
export const SCHEDULE_KIND_OPTIONS: readonly Command[] = [
  option<ScheduleKind>('calendar', 'Calendar', 'calendar'),
  option<ScheduleKind>('afterService', 'After service', 'afterService'),
  option<ScheduleKind>('perVisit', 'Per visit', 'perVisit'),
];

/**
 * The value the Repeat row emits for the explicit-date list.
 *
 * 'Specific dates' sits in the Repeat row rather than beside Calendar / After service / Per visit
 * because that is where a scheduler looks for "how often" — but it is a KIND, not a repeat: picking it
 * leaves the calendar branch entirely. It is spelled as a sentinel here so the row stays one control
 * and the dialog does the one translation at the edge.
 */
export const SPECIFIC_DATES_REPEAT_ID = 'dates';

export const SCHEDULE_REPEAT_OPTIONS: readonly Command[] = [
  option<ScheduleRepeat>('daily', 'Daily', 'daily'),
  option<ScheduleRepeat>('weekly', 'Weekly', 'weekly'),
  option<ScheduleRepeat>('fortnightly', 'Fortnightly', 'fortnightly'),
  option<ScheduleRepeat>('monthly', 'Monthly', 'monthly'),
  option<ScheduleRepeat>('quarterly', 'Quarterly', 'quarterly'),
  option<ScheduleRepeat>('yearly', 'Yearly', 'yearly'),
  // Last, after every pattern: it is the answer for a schedule no pattern describes.
  option<string>(SPECIFIC_DATES_REPEAT_ID, 'Specific dates', SPECIFIC_DATES_REPEAT_ID),
];

export const AFTER_SERVICE_UNIT_OPTIONS: readonly Command[] = [
  option('days', 'days', 'days'),
  option('months', 'months', 'months'),
  option('years', 'years', 'years'),
];

/** 'Due date' is the anniversary anchor: servicing early or late inside the window keeps the cycle. */
export const AFTER_SERVICE_ANCHOR_OPTIONS: readonly Command[] = [
  option<ScheduleAfterServiceAnchor>('completion', 'Completion date', 'completion'),
  option<ScheduleAfterServiceAnchor>('anniversary', 'Due date', 'anniversary'),
];

export const ENDS_OPTIONS: readonly Command[] = [
  option('never', 'Never', 'never'),
  option('onDate', 'On a date', 'onDate'),
  option('afterCount', 'After a count', 'afterCount'),
];

export const ORDINAL_OPTIONS: readonly Command[] = [
  option<ScheduleNth>('1', '1st', 1),
  option<ScheduleNth>('2', '2nd', 2),
  option<ScheduleNth>('3', '3rd', 3),
  option<ScheduleNth>('4', '4th', 4),
  option<ScheduleNth>('last', 'Last', 'last'),
];

export const WEEKDAY_OPTIONS: readonly Command[] = [
  option<ScheduleWeekday>('1', 'Monday', 1),
  option<ScheduleWeekday>('2', 'Tuesday', 2),
  option<ScheduleWeekday>('3', 'Wednesday', 3),
  option<ScheduleWeekday>('4', 'Thursday', 4),
  option<ScheduleWeekday>('5', 'Friday', 5),
  option<ScheduleWeekday>('6', 'Saturday', 6),
  option<ScheduleWeekday>('7', 'Sunday', 7),
];

/**
 * The five non-working-day policies, phrased as what happens to the job rather than as an enum name.
 * 'keep' leads because a contract date that must not move is the common case in maintenance work.
 */
export const NON_WORKING_DAY_OPTIONS: readonly Command[] = [
  option<ScheduleNonWorkingDayPolicy>('keep', 'Do it that day anyway', 'keep'),
  option<ScheduleNonWorkingDayPolicy>('skip', 'Skip that occurrence', 'skip'),
  option<ScheduleNonWorkingDayPolicy>('next', 'Move to the next working day', 'next'),
  option<ScheduleNonWorkingDayPolicy>('previous', 'Move to the previous working day', 'previous'),
  option<ScheduleNonWorkingDayPolicy>('nearest', 'Move to whichever working day is nearest', 'nearest'),
];

/** Short weekday captions for the seven equal toggles, Monday-first to match ISO weekday numbering. */
export const WEEKDAY_TOGGLES: readonly { weekday: ScheduleWeekday; label: string }[] = [
  { weekday: 1, label: 'Mo' },
  { weekday: 2, label: 'Tu' },
  { weekday: 3, label: 'We' },
  { weekday: 4, label: 'Th' },
  { weekday: 5, label: 'Fr' },
  { weekday: 6, label: 'Sa' },
  { weekday: 7, label: 'Su' },
];

const MONTH_LABELS: readonly string[] = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Jan–Jun then Jul–Dec, the two rows of month chips. */
export const MONTH_CHIP_ROWS: readonly (readonly { month: number; label: string }[])[] = [
  MONTH_LABELS.slice(0, 6).map((label, index) => ({ month: index + 1, label })),
  MONTH_LABELS.slice(6).map((label, index) => ({ month: index + 7, label })),
];

const DAY_LABELS: readonly string[] = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * 'Tue 1 Sep' — the preview and footer date format.
 *
 * Built from the numeric parts of the `YYYY-MM-DD` string, never by parsing it: `new Date('2026-09-01')`
 * is UTC midnight and renders as 31 August for anyone west of Greenwich. `Date.UTC` plus `getUTCDay`
 * keeps the weekday zone-free.
 */
export function formatScheduleDate(date: string): string {

  const [year, month, day] = date.split('-').map(Number);
  if (!year || !month || !day) return date;

  const weekday = DAY_LABELS[new Date(Date.UTC(year, month - 1, day)).getUTCDay()];

  return `${weekday} ${day} ${MONTH_LABELS[month - 1]}`;
}

/**
 * 'Mon 28 Sep (wk 40) · Mon 12 Oct (wk 42)' — the next-due strip.
 *
 * Customers quote calendar week numbers, so each date names its ISO week — but only the first date of
 * each week does, or a daily rule would repeat 'wk 36' four times. Weeks are compared by their Monday,
 * not their number, so a yearly rule landing in week 40 of two different years is labelled both times.
 */
export function formatSchedulePreview(dates: readonly string[]): string {

  let previousWeek: string | undefined;

  return dates
    .map(date => {
      const week = startOfIsoWeek(date);
      const label = week === previousWeek ? formatScheduleDate(date) : `${formatScheduleDate(date)} (wk ${isoWeekNumber(date)})`;
      previousWeek = week;
      return label;
    })
    .join(' · ');
}

/**
 * The ISO 8601 week number: weeks start Monday and week 1 holds the year's first Thursday, which is
 * what printed calendars and Outlook show. Some years run to week 53, so parity can repeat across New
 * Year — callers step real weeks rather than assuming numbers alternate.
 */
export function isoWeekNumber(date: string): number {

  const [year, month, day] = date.split('-').map(Number);
  if (!year || !month || !day) return 0;

  const thursday = new Date(Date.UTC(year, month - 1, day + 3 - ((new Date(Date.UTC(year, month - 1, day)).getUTCDay() + 6) % 7)));
  const weekOneThursday = new Date(Date.UTC(thursday.getUTCFullYear(), 0, 4));
  const weekOneMonday = Date.UTC(thursday.getUTCFullYear(), 0, 4 - ((weekOneThursday.getUTCDay() + 6) % 7));

  return Math.floor((thursday.getTime() - weekOneMonday) / (7 * 86_400_000)) + 1;
}

/**
 * The next `count` ISO week numbers of one parity, from the week containing `from` — 'wk 37, 39, 41'.
 * Steps real weeks, so a 53-week year shows 53 followed by 1 rather than an invented 55.
 */
export function isoParityWeeks(from: string, parity: 'isoOdd' | 'isoEven', count: number): number[] {

  const wanted = parity === 'isoOdd' ? 1 : 0;
  const weeks: number[] = [];
  let monday = startOfIsoWeek(from);

  while (weeks.length < count) {
    const week = isoWeekNumber(monday);
    if (week % 2 === wanted) weeks.push(week);
    monday = addCalendarDays(monday, 7);
  }

  return weeks;
}

/**
 * The Monday of the week containing `date`.
 *
 * The fortnight phase cards say when week A and week B start, and a week starts Monday everywhere in
 * this system (WKST=MO). An effective-from of Friday 28 August belongs to the week starting Monday 24
 * August, so naming the 28th as the start would contradict the rule the engine actually compiles.
 *
 * Label-only: the engine still owns where occurrences fall. Built from numeric parts via `Date.UTC` so
 * no timezone can shift the day.
 */
export function startOfIsoWeek(date: string): string {

  const [year, month, day] = date.split('-').map(Number);
  if (!year || !month || !day) return date;

  const utc = new Date(Date.UTC(year, month - 1, day));
  // getUTCDay is Sunday-first: Sunday must step back six days, not zero.
  const offset = (utc.getUTCDay() + 6) % 7;

  return addCalendarDays(date, -offset);
}

/** Whole-day arithmetic on numeric parts. Never `new Date(string)`, which parses as UTC midnight. */
export function addCalendarDays(date: string, days: number): string {

  const [year, month, day] = date.split('-').map(Number);
  if (!year || !month || !day) return date;

  return new Date(Date.UTC(year, month - 1, day + days)).toISOString().slice(0, 10);
}

/**
 * The months a quarterly rule lands in, given its anchor month and interval.
 *
 * Shown as a muted hint so "every 3 months" reads as the concrete Mar / Jun / Sep / Dec the customer
 * will see on their invoice. Purely a label — the engine still owns the real expansion.
 */
export function quarterlyAnchorMonths(anchorMonth: number, interval: number): string {

  if (interval < 1 || interval > 12) return '';

  const months: string[] = [];
  for (let month = anchorMonth; months.length < Math.floor(12 / interval); month += interval) {
    months.push(MONTH_LABELS[((month - 1) % 12 + 12) % 12]);
  }

  return months.join(' · ');
}

/**
 * The picked months, in calendar order — 'Jan · Apr · Jul · Oct'.
 *
 * Stands in for the interval hint once month chips are set: compile() drops INTERVAL when BYMONTH is
 * present, so these months ARE the rule, and naming a cadence beside them would state the opposite.
 */
export function fixedMonthLabels(months: readonly number[]): string {

  return Array.from(new Set(months))
    .filter(month => month >= 1 && month <= 12)
    .sort((a, b) => a - b)
    .map(month => MONTH_LABELS[month - 1])
    .join(' · ');
}
